import { useState } from 'react';
import { uploadImage, addVideoUrl, getMediaFileUrl } from '../api/media';
import MediaLibraryPicker from './MediaLibraryPicker';

const MAX_IMAGES = 3;
const MAX_VIDEOS = 1;

function getEmbedUrl(media) {
  const info = media.meta_info;
  if (!info) return null;
  if (info.platform === 'youtube') return `https://www.youtube.com/embed/${info.video_id}`;
  if (info.platform === 'vimeo') return `https://player.vimeo.com/video/${info.video_id}`;
  return null;
}

function isVideo(item) {
  return item.mediaType === 'video_url' || item.media_type === 'video_url';
}

export default function WorkoutIntroMediaSection({
  existingMedia,
  pendingMedia,
  onAddPending,
  onRemoveExisting,
  onRemovePending,
}) {
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [addingVideo, setAddingVideo] = useState(false);
  const [error, setError] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const allImages = [
    ...existingMedia.filter((m) => !isVideo(m)),
    ...pendingMedia.filter((m) => !isVideo(m)),
  ];
  const allVideos = [
    ...existingMedia.filter(isVideo),
    ...pendingMedia.filter(isVideo),
  ];

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (allImages.length >= MAX_IMAGES) return;
    setUploading(true);
    setError('');
    const previewUrl = URL.createObjectURL(file);
    try {
      const media = await uploadImage(file);
      onAddPending({ media, mediaType: 'image', previewUrl });
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  function handlePickFromLibrary(media) {
    setShowPicker(false);
    setError('');
    if (allImages.length >= MAX_IMAGES) return;
    const already = allImages.some((it) => (it.media?.id ?? it.media_id) === media.id);
    if (already) {
      setError('Dieses Bild ist bereits hinzugefügt.');
      return;
    }
    onAddPending({ media, mediaType: 'image' });
  }

  async function handleAddVideo() {
    if (!videoUrl.trim() || allVideos.length >= MAX_VIDEOS) return;
    setAddingVideo(true);
    setError('');
    try {
      const media = await addVideoUrl(videoUrl.trim());
      onAddPending({ media, mediaType: 'video_url' });
      setVideoUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingVideo(false);
    }
  }

  return (
    <div>
      <h6 className="card-subtitle mb-3 text-muted fw-semibold">Intro-Medien (Startbildschirm)</h6>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Images */}
      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="small fw-semibold">Bilder ({allImages.length}/{MAX_IMAGES})</span>
          <label className={`btn btn-sm btn-outline-secondary${allImages.length >= MAX_IMAGES || uploading ? ' disabled' : ''}`}>
            <i className="bi bi-upload me-1" />
            {uploading ? 'Lädt…' : 'Bild hochladen'}
            <input type="file" accept="image/*" hidden onChange={handleFileChange} disabled={allImages.length >= MAX_IMAGES || uploading} />
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowPicker(true)}
            disabled={allImages.length >= MAX_IMAGES}
          >
            <i className="bi bi-images me-1" />Aus Bibliothek
          </button>
        </div>

        {allImages.length > 0 && (
          <div className="d-flex flex-wrap gap-2">
            {existingMedia.filter((m) => !isVideo(m)).map((m) => (
              <ImageThumb
                key={m.id}
                src={getMediaFileUrl(m.media_id || m.media?.id)}
                isPending={false}
                onRemove={() => onRemoveExisting(m.id)}
              />
            ))}
            {pendingMedia.filter((m) => !isVideo(m)).map((m) => (
              <ImageThumb
                key={m.media.id}
                src={m.previewUrl || getMediaFileUrl(m.media.id)}
                isPending
                onRemove={() => onRemovePending(m.media.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video */}
      <div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="small fw-semibold">Video ({allVideos.length}/{MAX_VIDEOS})</span>
        </div>
        {allVideos.length < MAX_VIDEOS && (
          <div className="input-group input-group-sm mb-2">
            <input
              type="url"
              className="form-control"
              placeholder="YouTube- oder Vimeo-URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleAddVideo}
              disabled={!videoUrl.trim() || addingVideo}
            >
              {addingVideo ? 'Lädt…' : 'Hinzufügen'}
            </button>
          </div>
        )}
        <div className="d-flex flex-wrap gap-2">
          {existingMedia.filter(isVideo).map((m) => (
            <VideoThumb
              key={m.id}
              embedUrl={getEmbedUrl(m.media || m)}
              isPending={false}
              onRemove={() => onRemoveExisting(m.id)}
            />
          ))}
          {pendingMedia.filter(isVideo).map((m) => (
            <VideoThumb
              key={m.media.id}
              embedUrl={getEmbedUrl(m.media)}
              isPending
              onRemove={() => onRemovePending(m.media.id)}
            />
          ))}
        </div>
      </div>

      <MediaLibraryPicker
        show={showPicker}
        onPick={handlePickFromLibrary}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
}

function ImageThumb({ src, isPending, onRemove }) {
  return (
    <div className="position-relative" style={{ width: 100, height: 100 }}>
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
      {isPending && (
        <span className="badge bg-success position-absolute top-0 start-0 m-1" style={{ fontSize: '0.65rem' }}>neu</span>
      )}
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-0"
        style={{ width: 22, height: 22, lineHeight: 1 }}
        onClick={onRemove}
      >
        <i className="bi bi-x" />
      </button>
    </div>
  );
}

function VideoThumb({ embedUrl, isPending, onRemove }) {
  return (
    <div className="position-relative" style={{ width: 240, height: 135 }}>
      {embedUrl ? (
        <iframe
          src={embedUrl}
          title="Video"
          style={{ width: '100%', height: '100%', border: 'none', borderRadius: 6 }}
          allowFullScreen
        />
      ) : (
        <div className="d-flex align-items-center justify-content-center bg-secondary rounded" style={{ width: '100%', height: '100%' }}>
          <i className="bi bi-play-circle text-white fs-3" />
        </div>
      )}
      {isPending && (
        <span className="badge bg-success position-absolute top-0 start-0 m-1" style={{ fontSize: '0.65rem' }}>neu</span>
      )}
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-0"
        style={{ width: 22, height: 22, lineHeight: 1 }}
        onClick={onRemove}
      >
        <i className="bi bi-x" />
      </button>
    </div>
  );
}
